import { InnerBox } from "@/components/elements/InnerBox";
import { Grid, Link, Stack, Typography } from "@mui/material";
import { FC, useState } from "react";
import { JobFormTitle } from "@/features/job/components/job-form/JobFormTitle";
import { TJobCase, TJobForm } from "@/features/job/types/JobForm";
import { black, secondary, text } from "@/theme/colors";
import { Button } from "@/components/elements/Button";
import { JOB_STATUS } from "@/types/enum";
import { AppRoutes } from "@/routes/config";
import { useNavigate, useParams } from "react-router-dom";
import { getDynamicRoute } from "@/utils/url";
import { rounded } from "@/theme/variables";
import { DATE_FORMAT } from "@/constants/datetime";
import dayjs from "dayjs";
import { CaseTagStatus } from "@/features/case/components/CaseTagStatus";
import { TCaseStatus } from "@/features/case/constants/caseStatus";
import { typography } from "@/theme/typography";

type TJobCaseDetailProps = {
  defaultValues?: Partial<TJobForm>;
};

export const JobCaseDetail: FC<TJobCaseDetailProps> = ({ defaultValues }) => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const PAGE_SIZE = 6;

  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const handleLoadMore = () => {
    setVisibleCount((prev) => prev + PAGE_SIZE);
  };

  const navigateCreateCase = () => {
    const route = getDynamicRoute(AppRoutes.caseCreate, { jobId: jobId });
    navigate(route);
  };

  const handleCaseClick = (item: TJobCase) => {
    const caseId = item.id;
    if (jobId && caseId) {
      const caseEditRoute = getDynamicRoute(AppRoutes.caseEdit, {
        jobId: String(jobId),
        caseId: String(caseId),
      });

      window.open(caseEditRoute, "_blank");
    }
  };

  const getJobStatusMessageWhenNoCases = (status?: string): string => {
    switch (status) {
      case JOB_STATUS.DRAFT:
        return "求人の必須項目を入力し、ステータスを「募集予定」または「募集中」に変更することで案件を開始できます。";

      case JOB_STATUS.PAUSED:
      case JOB_STATUS.CLOSED:
        return "案件が作成されておりません。案件を始めるには求人のステータスを「募集中」に戻してください。";

      default:
        return "案件が作成されておりません。";
    }
  };

  return (
    <Stack gap={2}>
      <InnerBox
        title={<JobFormTitle text="案件参加状況" />}
        rightContent={
          (defaultValues?.status === JOB_STATUS.OPEN || defaultValues?.status === JOB_STATUS.SCHEDULED) && (
            <Button size="sm" variant="outline" onClick={navigateCreateCase}>
              案件の作成（人材候補の割当）
            </Button>
          )
        }
        padding="24px"
        showDivider={false}
      >
        <Grid container spacing={2} mt="11px">
          {defaultValues?.cases && defaultValues.cases.length > 0 ? (
            <>
              {defaultValues.cases.slice(0, visibleCount).map((item, index) => (
                <Grid item xs={4} key={index}>
                  <Stack
                    p={2}
                    border={`1px solid ${black[99]}`}
                    borderRadius={rounded.sm}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    spacing="10px"
                  >
                    <Stack>
                      {item?.name && (
                        <Typography
                          variant="body14"
                          color={black[30]}
                          sx={{
                            mb: 0.5,
                            fontFamily: "inherit",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                          }}
                        >
                          {item.name}
                        </Typography>
                      )}
                      {item?.createdAt && (
                        <Typography variant="cap12" color={black[30]} sx={{ mb: 0.5 }}>
                          {dayjs(item.createdAt).format(DATE_FORMAT)}
                        </Typography>
                      )}
                    </Stack>

                    <Stack alignItems="flex-start">
                      {item?.progressStatus && <CaseTagStatus status={item.progressStatus as TCaseStatus} isAllFailed={item.isAllFailed} />}
                      <Link
                        component="button"
                        variant="body1"
                        onClick={() => handleCaseClick(item)}
                        sx={{
                          mt: "7px",
                          textDecoration: "none",
                          color: secondary[80],
                          cursor: "pointer",
                          ...typography.cap12,
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        詳細→
                      </Link>
                    </Stack>
                  </Stack>
                </Grid>
              ))}
              {visibleCount < defaultValues.cases.length && (
                <Stack flex={1} alignItems="center" mt={2} direction="row" justifyContent="center">
                  <Typography
                    onClick={handleLoadMore}
                    sx={{
                      mt: "7px",
                      textDecoration: "none",
                      color: secondary[80],
                      cursor: "pointer",
                      ...typography.body14,
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    さらに表示
                  </Typography>
                </Stack>
              )}
            </>
          ) : (
            <Stack flexDirection="row" flex={1} justifyContent="center" mt={1}>
              <Typography variant="sub16Bold" color={text.tertiary}>
                {getJobStatusMessageWhenNoCases(defaultValues?.status)}
              </Typography>
            </Stack>
          )}
        </Grid>
      </InnerBox>
    </Stack>
  );
};
